import store from '@state/store';
import ReportModel from '@models/Report';
import DriverModel from '@models/Driver';
import JobModel from '@models/Job';
import ParamModel from '@models/Param';
import QuoteModel from '@models/Quote';

let routes = [
  {
    path: '/',
    name: 'home.index',
    component: () => lazyLoadView(import('@views/home/home')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        ParamModel.params({
          select_many: ['areas', 'locations', 'm_areas', 'job-statuses'].join(','),
        })
          .first()
          .then((response) => {
            routeTo.params.areas = response.areas;
            routeTo.params.locations = response.locations;
            routeTo.params.m_areas = response.m_areas;
            routeTo.params.jobStatuses = response.job_statuses;
            next();
          })
          .catch(() => next({ name: 'home.index' }));
      },
    },
    props: (route) => ({
      areas: route.params.areas,
      locations: route.params.locations,
      jobStatuses: route.params.jobStatuses,
      mAreas: route.params.m_areas,
    }),
  },
  {
    path: '/news',
    name: 'news.index',
    component: () => lazyLoadView(import('@views/news/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/downloads',
    name: 'downloads.index',
    component: () => lazyLoadView(import('@views/downloads/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/download-categories',
    name: 'downloadCategories.index',
    component: () => lazyLoadView(import('@views/download-categories/index')),
    meta: {
      authRequired: true,
      can: 'superAdministrate',
    },
  },
  {
    path: '/download-categories/create',
    name: 'downloadCategories.create',
    component: () => lazyLoadView(import('@views/download-categories/create')),
    meta: {
      authRequired: true,
      can: 'superAdministrate',
    },
  },
  {
    path: '/download-categories/:id/edit',
    name: 'downloadCategories.edit',
    component: () => lazyLoadView(import('@views/download-categories/edit')),
    meta: {
      authRequired: true,
      can: 'superAdministrate',
    },
  },
  // Points of Interest
  {
    path: '/point-of-interests',
    name: 'pointOfInterests.index',
    component: () => lazyLoadView(import('@views/pointOfInterests/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/news/create',
    name: 'news.create',
    component: () => lazyLoadView(import('@views/news/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/news/edit/:id',
    name: 'news.edit',
    component: () => lazyLoadView(import('@views/news/edit')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/news/show/:id',
    name: 'news.show',
    component: () => lazyLoadView(import('@views/news/show')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/login',
    name: 'login.index',
    component: () => lazyLoadView(import('@views/auth/login')),
    meta: {
      guestRequired: true,
    },
  },
  {
    path: '/reports',
    name: 'reports.index',
    component: () => lazyLoadView(import('@views/reports/index')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        ParamModel.params({
          select_many: ['locations', 'm_areas'].join(','),
        })
          .first()
          .then((response) => {
            routeTo.params.locations = response.locations;
            routeTo.params.areas = response.m_areas;
            next();
          })
          .catch(() => next({ name: 'reports.index' }));
      },
    },
    props: (route) => ({
      locations: route.params.locations,
      areas: route.params.areas,
    }),
  },
  {
    path: '/reports/create',
    name: 'reports.create',
    component: () => lazyLoadView(import('@views/reports/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/reports/:id/edit',
    name: 'reports.edit',
    component: () => import('@views/reports/edit'),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        ReportModel.find(routeTo.params.id)
          .then((response) => {
            routeTo.params.report = response.report;
            routeTo.params.carUses = response.car_uses;
            routeTo.params.serviceTimes = response.service_times.data;
            routeTo.params.expenses = response.expenses;
            routeTo.params.allExpensesHaveScan = response.all_expenses_have_scan;
            routeTo.params.dailyAllowances = response.daily_allowances;
            next();
          })
          .catch(() => next({ name: 'reports.index' }));
      },
    },
    props: (route) => ({
      report: route.params.report,
      carUses: route.params.carUses,
      serviceTimes: route.params.serviceTimes,
      expenses: route.params.expenses,
      dailyAllowances: route.params.dailyAllowances,
      summedServiceTime: route.params.summedServiceTime,
      allExpensesHaveScan: route.params.allExpensesHaveScan,
    }),
  },
  {
    path: '/car-times',
    name: 'carTimes.index',
    component: () => lazyLoadView(import('@views/carTimes/index')),
    meta: {
      authRequired: true,
      can: 'doAccounting',
    },
  },
  {
    path: '/profile',
    name: 'profile.index',
    component: () => import('@views/profile/profile'),
    meta: {
      authRequired: true,
    },
  },
  // JOBS
  {
    path: '/jobs',
    name: 'jobs.index',
    component: () => lazyLoadView(import('@views/jobs/index')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        ParamModel.params({
          select_many: ['locations', 'm_areas', 'job-statuses'].join(','),
        })
          .first()
          .then((response) => {
            routeTo.params.locations = response.locations;
            routeTo.params.areas = response.m_areas;
            routeTo.params.jobStatuses = response.job_statuses;
            next();
          })
          .catch(() => next({ name: 'jobs.index' }));
      },
    },
    props: (route) => ({
      locations: route.params.locations,
      areas: route.params.areas,
      jobStatuses: route.params.jobStatuses,
      pageTitle: 'Jobs',
    }),
  },
  {
    path: '/jobs/planning-view',
    name: 'jobs.planning.index',
    component: () => lazyLoadView(import('@views/jobs/planning')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        ParamModel.params({
          select_many: ['locations', 'm_areas', 'job-statuses'].join(','),
        })
          .first()
          .then((response) => {
            routeTo.params.locations = response.locations;
            routeTo.params.areas = response.m_areas;
            routeTo.params.jobStatuses = response.job_statuses;
            next();
          })
          .catch(() => next({ name: 'jobs.index' }));
      },
    },
    props: (route) => ({
      locations: route.params.locations,
      areas: route.params.areas,
      pageTitle: 'Planning',
    }),
  },
  {
    path: '/jobs/create',
    name: 'jobs.create',
    component: () => lazyLoadView(import('@views/jobs/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/jobs/:id/edit',
    name: 'jobs.edit',
    component: () => import('@views/jobs/edit'),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        JobModel.find(routeTo.params.id)
          .then((response) => {
            routeTo.params.job = response.job;
            next();
          })
          .catch(() => next({ name: 'jobs.index' }));
      },
    },
    props: (route) => ({
      job: route.params.job,
    }),
  },
  // CARS
  {
    path: '/cars',
    name: 'cars.index',
    component: () => lazyLoadView(import('@views/cars/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/cars/create',
    name: 'cars.create',
    component: () => lazyLoadView(import('@views/cars/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/cars/:id/edit',
    name: 'cars.edit',
    component: () => import('@views/cars/edit'),
    meta: {
      authRequired: true,
    },
  },
  // Locations
  {
    path: '/locations',
    name: 'locations.index',
    component: () => lazyLoadView(import('@views/locations/index')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        ParamModel.params({
          select_many: ['areas'].join(','),
        })
          .first()
          .then((response) => {
            routeTo.params.areas = response.areas;
            next();
          })
          .catch(() => next({ name: 'locations.index' }));
      },
    },
    props: (route) => ({
      areas: route.params.areas,
    }),
  },
  {
    path: '/locations/create',
    name: 'locations.create',
    component: () => lazyLoadView(import('@views/locations/create')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        ParamModel.params({
          select_many: ['areas'].join(','),
        })
          .first()
          .then((response) => {
            routeTo.params.areas = response.areas;
            next();
          })
          .catch(() => next({ name: 'locations.index' }));
      },
    },
    props: (route) => ({
      areas: route.params.areas,
    }),
  },
  {
    path: '/locations/:id/edit',
    name: 'locations.edit',
    component: () => import('@views/locations/edit'),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        ParamModel.params({
          select_many: ['areas'].join(','),
        })
          .first()
          .then((response) => {
            routeTo.params.areas = response.areas;
            next();
          })
          .catch(() => next({ name: 'locations.index' }));
      },
    },
    props: (route) => ({
      areas: route.params.areas,
    }),
  },
  // COST TYPES
  {
    path: '/cost-types',
    name: 'costTypes.index',
    component: () => lazyLoadView(import('@views/costTypes/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/cost-types/create',
    name: 'costTypes.create',
    component: () => lazyLoadView(import('@views/costTypes/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/cost-types/:id/edit',
    name: 'costTypes.edit',
    component: () => import('@views/costTypes/edit'),
    meta: {
      authRequired: true,
    },
  },
  // CAR TYPES
  {
    path: '/car-types',
    name: 'carTypes.index',
    component: () => lazyLoadView(import('@views/carTypes/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/car-types/create',
    name: 'carTypes.create',
    component: () => lazyLoadView(import('@views/carTypes/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/car-types/:id/edit',
    name: 'carTypes.edit',
    component: () => import('@views/carTypes/edit'),
    meta: {
      authRequired: true,
    },
  },
  // DRIVERS
  {
    path: '/drivers',
    name: 'drivers.index',
    component: () => lazyLoadView(import('@views/drivers/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/drivers/create',
    name: 'drivers.create',
    component: () => lazyLoadView(import('@views/drivers/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/drivers/:id/edit',
    name: 'drivers.edit',
    component: () => import('@views/drivers/edit'),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        DriverModel.find(routeTo.params.id)
          .then((response) => {
            let user = response.user;
            user.verified = user.verification_token === null;
            routeTo.params.user = user;
            next();
          })
          .catch(() => next({ name: 'drivers.index' }));
      },
    },
    props: (route) => ({
      user: route.params.user,
    }),
  },
  // companies
  {
    path: '/companies',
    name: 'companies.index',
    component: () => lazyLoadView(import('@views/companies/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/companies/create',
    name: 'companies.create',
    component: () => lazyLoadView(import('@views/companies/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/companies/:id/edit',
    name: 'companies.edit',
    component: () => import('@views/companies/edit'),
    meta: {
      authRequired: true,
    },
  },
  // Allowances
  {
    path: '/allowances',
    name: 'allowances.index',
    component: () => lazyLoadView(import('@views/allowances/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/allowances/create',
    name: 'allowances.create',
    component: () => lazyLoadView(import('@views/allowances/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/allowances/:id/edit',
    name: 'allowances.edit',
    component: () => import('@views/allowances/edit'),
    meta: {
      authRequired: true,
    },
  },
  // Countries
  {
    path: '/countries',
    name: 'countries.index',
    component: () => lazyLoadView(import('@views/countries/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/countries/create',
    name: 'countries.create',
    component: () => lazyLoadView(import('@views/countries/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/countries/:id/edit',
    name: 'countries.edit',
    component: () => import('@views/countries/edit'),
    meta: {
      authRequired: true,
    },
  },
  // contacts
  {
    path: '/contacts',
    name: 'contacts.index',
    component: () => lazyLoadView(import('@views/contacts/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/contacts/create',
    name: 'contacts.create',
    component: () => lazyLoadView(import('@views/contacts/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/contacts/:id/edit',
    name: 'contacts.edit',
    component: () => import('@views/contacts/edit'),
    meta: {
      authRequired: true,
    },
  },
  // vips
  {
    path: '/vips',
    name: 'vips.index',
    component: () => lazyLoadView(import('@views/vips/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/vips/create',
    name: 'vips.create',
    component: () => lazyLoadView(import('@views/vips/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/vips/:id/edit',
    name: 'vips.edit',
    component: () => import('@views/vips/edit'),
    meta: {
      authRequired: true,
    },
  },
  // Service providers
  {
    path: '/service-providers',
    name: 'serviceProviders.index',
    component: () => lazyLoadView(import('@views/serviceProviders/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/service-providers/create',
    name: 'serviceProviders.create',
    component: () => lazyLoadView(import('@views/serviceProviders/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/service-providers/:id/edit',
    name: 'serviceProviders.edit',
    component: () => import('@views/serviceProviders/edit'),
    meta: {
      authRequired: true,
    },
  },
  // Quotes
  {
    path: '/quotes',
    name: 'quotes.index',
    component: () => lazyLoadView(import('@views/quotes/index')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        ParamModel.params({
          select_many: ['quote_position_descriptions'].join(','),
        })
          .first()
          .then((response) => {
            routeTo.params.quote_position_descriptions = response.quote_position_descriptions;
            next();
          })
          .catch(() => next({ name: 'quotes.index' }));
      },
    },
    props: (route) => ({
      quote_position_descriptions: route.params.quote_position_descriptions,
      pageTitle: 'Quotes',
    }),
  },
  {
    path: '/quotes/create',
    name: 'quotes.create',
    component: () => lazyLoadView(import('@views/quotes/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/quotes/:id/edit',
    name: 'quotes.edit',
    component: () => import('@views/quotes/edit'),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        QuoteModel.find(routeTo.params.id)
          .then((response) => {
            routeTo.params.quote = response.quote;
            routeTo.params.quotePositions = response.quote.quote_positions;
            next();
          })
          .catch(() => next({ name: 'quotes.index' }));
      },
    },
    props: (route) => ({
      quote: route.params.quote,
      quotePositions: route.params.quotePositions,
    }),
  },
  // admins
  {
    path: '/admins',
    name: 'admins.index',
    component: () => lazyLoadView(import('@views/admins/index')),
    meta: {
      authRequired: true,
      can: 'superAdministrate',
    },
  },
  {
    path: '/admins/create',
    name: 'admins.create',
    component: () => lazyLoadView(import('@views/admins/create')),
    meta: {
      authRequired: true,
      can: 'superAdministrate',
    },
  },
  {
    path: '/admins/:id/edit',
    name: 'admins.edit',
    component: () => import('@views/admins/edit'),
    meta: {
      authRequired: true,
      can: 'superAdministrate',
    },
  },
  {
    path: '/leaves',
    name: 'leaves.index',
    component: () => import('@views/leaves/index'),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        ParamModel.params({
          select_many: ['locations', 'm_areas'].join(','),
        })
          .first()
          .then((response) => {
            routeTo.params.locations = response.locations;
            routeTo.params.areas = response.m_areas;
            next();
          })
          .catch(() => next({ name: 'leaves.index' }));
      },
    },
    props: (route) => ({
      locations: route.params.locations,
      areas: route.params.areas,
    }),
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        store.dispatch('auth/logOut');
        const authRequiredOnPreviousRoute = routeFrom.matched.some((route) => route.meta.authRequired);
        // Navigate back to previous page, or home as a fallback
        next(authRequiredOnPreviousRoute ? { name: 'login.index' } : { ...routeFrom });
      },
    },
  },
  {
    path: '/404',
    name: '404',
    component: require('@views/errors/_404').default,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
];

[
  { entity: 'CarOwner', path: 'car-owners', name: 'Car Owners', createLabel: 'Create Car Owner' },
].forEach((el) => {
  const meta = {
    authRequired: true,
    entity: el.entity,
    path: el.path,
    name: el.name,
    createLabel: el.createLabel,
    breadcrumbName: el.breadcrumbName || null,
    fields: el.fields || [],
  };
  routes = routes.concat([
    {
      path: `/${el.path}`,
      name: `${el.path}.index`,
      component: () => lazyLoadView(import('@views/universal/index')),
      meta,
    },

    {
      path: `/${el.path}/create`,
      name: `${el.path}.create`,
      component: () => lazyLoadView(import('@views/universal/create')),
      meta,
    },

    {
      path: `/${el.path}/:id`,
      name: `${el.path}.edit`,
      component: () => import('@views/universal/edit'),
      meta,
    },
  ]);
});

export default routes;

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@views/_loading').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: require('@views/_timeout').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  });

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children);
    },
  });
}
